import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import axios from "axios"
import { Container, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, IconButton, Typography, LinearProgress, useTheme, useMediaQuery, Collapse, List, ListItem, ListItemText, Divider, AppBar, Toolbar, Button, Drawer, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, CircularProgress } from "@mui/material"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { storage } from "../firebase"
import RefreshIcon from "@mui/icons-material/Refresh"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import Brightness4Icon from "@mui/icons-material/Brightness4"
import Brightness7Icon from "@mui/icons-material/Brightness7"
import LogoutIcon from "@mui/icons-material/Logout"
import HelpIcon from "@mui/icons-material/Help"
import SearchIcon from "@mui/icons-material/Search"
import ReactMarkdown from "react-markdown"
import CloseIcon from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import SmsIcon from "@mui/icons-material/Sms"
import md5 from "crypto-js/md5"

const stringToColor = string => {
  let hash = 0
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Generate darker colors by mixing with a darker base
  const pastelize = hex => {
    const mix = [100, 100, 100] // Darker base color instead of white
    const weight = 0.8 // Increased weight for more saturation

    const r = Math.round(hash & 255)
    const g = Math.round((hash >> 8) & 255)
    const b = Math.round((hash >> 16) & 255)

    const result = [Math.round(r * weight + mix[0] * (1 - weight)), Math.round(g * weight + mix[1] * (1 - weight)), Math.round(b * weight + mix[2] * (1 - weight))]

    return `#${result
      .map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? "0" + hex : hex
      })
      .join("")}`
  }

  return pastelize(hash)
}

const getInitials = (name, email) => {
  if (name && name.trim()) {
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2)
  }
  // If no name, use first letter of email
  return email ? email[0].toUpperCase() : "?"
}

const createGravatarUrl = email => {
  const hash = md5(email.toLowerCase().trim()).toString()
  return `https://www.gravatar.com/avatar/${hash}?s=40&d=404`
}

const MemberAvatar = ({ member, mode, width = 48, height = 48, fontSize = "1.5rem", onClick }) => {
  const [failedCustom, setFailedCustom] = useState(false)
  const [failedGravatar, setFailedGravatar] = useState(false)

  useEffect(() => {
    setFailedCustom(false)
    setFailedGravatar(false)
  }, [member?.email, member?.photoUrl])

  if (!member) return null

  const initials = getInitials(member.FNAME ? `${member.FNAME} ${member.LNAME || ""}`.trim() : "", member.email)
  const gravatarUrl = createGravatarUrl(member.email)

  let currentSrc = null
  if (member.photoUrl && !failedCustom) {
    currentSrc = member.photoUrl
  } else if (!failedGravatar) {
    currentSrc = gravatarUrl
  }

  const handleError = () => {
    if (member.photoUrl && !failedCustom) {
      setFailedCustom(true)
    } else {
      setFailedGravatar(true)
    }
  }

  return (
    <Avatar
      src={currentSrc}
      alt={member.FNAME ? `${member.FNAME} ${member.LNAME || ""}`.trim() : member.email}
      onClick={onClick}
      sx={{
        bgcolor: stringToColor(member.FNAME || member.email || ""),
        width,
        height,
        fontSize,
        fontWeight: 900,
        color: "white",
        cursor: onClick ? "pointer" : "default",
        border: mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "none"
      }}
      onError={handleError}
    >
      {initials}
    </Avatar>
  )
}

const EditDialog = ({ open, handleClose, member, handleSave, currentUser, mode }) => {
  const [formData, setFormData] = useState({
    FNAME: "",
    LNAME: "",
    PHONE: "",
    MMERGE6: "",
    photoUrl: ""
  })

  const [errors, setErrors] = useState({})
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState("")

  useEffect(() => {
    if (member) {
      setFormData({
        FNAME: member.FNAME || "",
        LNAME: member.LNAME || "",
        PHONE: member.PHONE || "",
        MMERGE6: member.MMERGE6 || "",
        photoUrl: member.photoUrl || ""
      })
      setPreviewUrl("")
      setErrors({}) // Clear errors when dialog opens
    }
  }, [member])

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.FNAME.trim()) {
      newErrors.FNAME = "First name is required"
    } else if (!/^[a-zA-Z\s-']+$/.test(formData.FNAME.trim())) {
      newErrors.FNAME = "Invalid characters in first name"
    }

    if (!formData.LNAME.trim()) {
      newErrors.LNAME = "Last name is required"
    } else if (!/^[a-zA-Z\s-']+$/.test(formData.LNAME.trim())) {
      newErrors.LNAME = "Invalid characters in last name"
    }

    // Phone validation (optional field)
    if (formData.PHONE) {
      const digitsOnly = formData.PHONE.replace(/\D/g, "")
      if (digitsOnly.length < 10) {
        newErrors.PHONE = "Phone number must have at least 10 digits"
      } else if (!/^[\d\s()-]+$/.test(formData.PHONE.trim())) {
        newErrors.PHONE = "Only numbers, spaces, dashes, and parentheses allowed"
      }
    }

    // Birthday validation (optional field)
    if (formData.MMERGE6) {
      const birthdayRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/
      if (!birthdayRegex.test(formData.MMERGE6.trim())) {
        newErrors.MMERGE6 = "Please use MM/DD format"
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const processImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")
          const size = Math.min(img.width, img.height)
          const x = (img.width - size) / 2
          const y = (img.height - size) / 2
          canvas.width = 300
          canvas.height = 300
          ctx.drawImage(img, x, y, size, size, 0, 0, 300, 300)
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error("Canvas to Blob conversion failed"))
              }
            },
            "image/jpeg",
            0.8
          )
        }
        img.onerror = () => reject(new Error("Failed to load image"))
        img.src = e.target.result
      }
      reader.onerror = () => reject(new Error("Failed to read file"))
      reader.readAsDataURL(file)
    })
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!currentUser || !currentUser.uid) {
      setErrors(prev => ({ ...prev, photo: "You must be logged in to upload a photo." }))
      return
    }

    try {
      setUploading(true)
      setErrors(prev => ({ ...prev, photo: null }))
      const compressedBlob = await processImage(file)
      
      // Upload directly to Firebase Storage under avatars/{uid}.jpg
      const storageRef = ref(storage, `avatars/${currentUser.uid}.jpg`)
      await uploadBytes(storageRef, compressedBlob, { contentType: "image/jpeg" })
      const downloadUrl = await getDownloadURL(storageRef)
      
      setFormData(prev => ({ ...prev, photoUrl: downloadUrl }))
      setPreviewUrl(URL.createObjectURL(compressedBlob))
    } catch (err) {
      console.error("Error processing/uploading image:", err)
      setErrors(prev => ({ ...prev, photo: "Failed to upload image. Please try again." }))
    } finally {
      setUploading(false)
    }
  }

  const handleRemovePhoto = () => {
    setFormData(prev => ({ ...prev, photoUrl: "" }))
    setPreviewUrl("")
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (validateForm()) {
      // Trim whitespace from all fields
      const trimmedData = Object.keys(formData).reduce((acc, key) => {
        acc[key] = typeof formData[key] === "string" ? formData[key].trim() : formData[key]
        return acc
      }, {})
      handleSave(trimmedData)
      handleClose()
    }
  }

  const handlePhoneChange = e => {
    const input = e.target.value.replace(/\D/g, "").substring(0, 10)
    let formatted = input
    if (input.length > 6) {
      formatted = `(${input.slice(0, 3)})${input.slice(3, 6)}-${input.slice(6)}`
    } else if (input.length > 3) {
      formatted = `(${input.slice(0, 3)})${input.slice(3)}`
    }
    setFormData({ ...formData, PHONE: formatted })
  }

  const handleBirthdayChange = e => {
    const input = e.target.value.replace(/\D/g, "").substring(0, 4)
    let formatted = input
    if (input.length > 2) {
      formatted = `${input.slice(0, 2)}/${input.slice(2)}`
    }
    setFormData({ ...formData, MMERGE6: formatted })
  }

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (uploading) return
        handleClose()
      }}
      disableEscapeKeyDown={uploading}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Edit Your Information</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 2 }}>
        <Box sx={{ position: "relative", mb: 2 }}>
          <Avatar
            src={previewUrl || formData.photoUrl || (member ? createGravatarUrl(member.email) : "")}
            sx={{
              width: 100,
              height: 100,
              bgcolor: stringToColor(formData.FNAME || member?.email || ""),
              fontSize: "2.5rem",
              fontWeight: 900,
              color: "white",
              border: mode === "dark" ? "2px solid rgba(255,255,255,0.2)" : "2px solid #e0e0e0"
            }}
            onError={e => { e.target.src = null }}
          >
            {getInitials(formData.FNAME ? `${formData.FNAME} ${formData.LNAME || ""}`.trim() : "", member?.email || "")}
          </Avatar>
          {uploading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 100,
                height: 100,
                borderRadius: "50%",
                bgcolor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <CircularProgress size={30} sx={{ color: "white" }} />
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button variant="outlined" component="label" disabled={uploading} size="small">
            Upload Photo
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
          {(formData.photoUrl || previewUrl) && (
            <Button variant="outlined" color="error" onClick={handleRemovePhoto} disabled={uploading} size="small">
              Remove
            </Button>
          )}
        </Box>
        {errors.photo && (
          <Typography color="error" variant="caption" sx={{ mb: 2 }}>
            {errors.photo}
          </Typography>
        )}

        <Box sx={{ width: "100%" }}>
          <TextField label="First Name" value={formData.FNAME} onChange={e => setFormData({ ...formData, FNAME: e.target.value })} fullWidth margin="normal" error={!!errors.FNAME} helperText={errors.FNAME} required />
          <TextField label="Last Name" value={formData.LNAME} onChange={e => setFormData({ ...formData, LNAME: e.target.value })} fullWidth margin="normal" error={!!errors.LNAME} helperText={errors.LNAME} required />
          <TextField label="Phone" value={formData.PHONE} onChange={handlePhoneChange} fullWidth margin="normal" error={!!errors.PHONE} helperText={errors.PHONE || "Format: (555) 555-5555"} />
          <TextField label="Birthday (MM/DD)" value={formData.MMERGE6} onChange={handleBirthdayChange} fullWidth margin="normal" error={!!errors.MMERGE6} helperText={errors.MMERGE6 || "Example: 01/25"} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={uploading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const Directory = ({ toggleColorMode, mode }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [orderBy, setOrderBy] = useState("name")
  const [order, setOrder] = useState("asc")
  const [searchTerm, setSearchTerm] = useState("")
  const [helpOpen, setHelpOpen] = useState(false)
  const [expandedRow, setExpandedRow] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  // Define columns array
  const columns = [
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "phone", label: "Phone" },
    { id: "birthday", label: "Birthday" }
  ]

  const handleSort = property => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }

  const formatDate = dateStr => {
    if (!dateStr) return ""
    try {
      const [month, day] = dateStr.split("/")
      return `${month}/${day}`
    } catch (e) {
      return dateStr
    }
  }

  const formatPhone = phone => {
    if (!phone) return ""
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, "")
    // Format as (XXX)XXX-XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)})${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
  }

  // Extract fetch logic into reusable function
  const fetchDirectoryData = useCallback(
    async signal => {
      setLoading(true)
      try {
        const response = await axios.get("/.netlify/functions/directory", {
          signal
        })
        const result = response.data
        const members = Array.isArray(result) ? result : result.members || []
        setData(members)
        setError(null)
      } catch (err) {
        if (err.name === "CanceledError" || axios.isCancel(err)) return
        setError(err.response?.data?.error || err.message || "Failed to load directory members")
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchDirectoryData(controller.signal)
    return () => controller.abort()
  }, [fetchDirectoryData])

  useEffect(() => {
    // This will update when user auth state changes
    if (user && user.email) {
      // Intentionally left blank for potential future auth-driven state management
    }
  }, [user])

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  const filteredAndSortedMembers = React.useMemo(() => {
    return [...data]
      .filter(member => {
        if (!member) return false
        const searchLower = searchTerm.toLowerCase()
        const name = member.FNAME ? `${member.FNAME} ${member.LNAME || ""}`.trim() : ""
        return name.toLowerCase().includes(searchLower) || (member.email || "").toLowerCase().includes(searchLower)
      })
      .sort((a, b) => {
        const isAsc = order === "asc"
        const getName = m => (m.FNAME ? `${m.FNAME} ${m.LNAME || ""}`.trim() : "")

        switch (orderBy) {
          case "name":
            return isAsc ? getName(a).localeCompare(getName(b)) : getName(b).localeCompare(getName(a))
          case "email":
            return isAsc ? (a.email || "").localeCompare(b.email || "") : (b.email || "").localeCompare(a.email || "")
          case "birthday":
            // Handle empty birthdays
            if (!a.MMERGE6 && !b.MMERGE6) return 0
            if (!a.MMERGE6) return isAsc ? 1 : -1
            if (!b.MMERGE6) return isAsc ? -1 : 1
            // Sort by month/day
            const [aMonth, aDay] = a.MMERGE6.split("/").map(Number)
            const [bMonth, bDay] = b.MMERGE6.split("/").map(Number)
            // Compare months first
            if (aMonth !== bMonth) {
              return isAsc ? aMonth - bMonth : bMonth - aMonth
            }
            // If months are the same, compare days
            return isAsc ? aDay - bDay : bDay - aDay
          default:
            return 0
        }
      })
  }, [data, orderBy, order, searchTerm])



  const handleRowClick = index => {
    setExpandedRow(expandedRow === index ? null : index)
  }

  const MobileRow = ({ member, index }) => {
    const isExpanded = expandedRow === index
    const name = member.FNAME ? `${member.FNAME} ${member.LNAME || ""}`.trim() : member.email

    return (
      <>
        <TableRow
          hover
          onClick={() => handleRowClick(index)}
          sx={{
            cursor: "pointer",
            backgroundColor: mode === "light" ? (index % 2 === 0 ? "white" : "#fafafa") : index % 2 === 0 ? "#121212" : "#1a1a1a",
            "&:hover": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#202020"
            }
          }}
        >
          <TableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <MemberAvatar member={member} mode={mode} width={48} height={48} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontSize: "1.1rem" }}>{name}</Typography>
              </Box>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <List>
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                      <Typography sx={{ fontSize: "1.1rem", wordBreak: 'break-all' }}>{member.email}</Typography>
                      <IconButton
                        component="a"
                        href={`mailto:${member.email}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ bgcolor: "primary.light", color: "white", "&:hover": { bgcolor: "primary.main" } }}
                      >
                        <EmailIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                      {member.PHONE && member.PHONE.replace(/\D/g, "").length >= 10 ? (
                        <>
                          <Typography sx={{ fontSize: "1.1rem" }}>{formatPhone(member.PHONE)}</Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              component="a"
                              href={`tel:${member.PHONE.replace(/\D/g, "")}`}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ bgcolor: "success.light", color: "white", "&:hover": { bgcolor: "success.main" } }}
                            >
                              <PhoneIcon />
                            </IconButton>
                            <IconButton
                              component="a"
                              href={`sms:${member.PHONE.replace(/\D/g, "")}`}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ bgcolor: "secondary.light", color: "white", "&:hover": { bgcolor: "secondary.main" } }}
                            >
                              <SmsIcon />
                            </IconButton>
                          </Box>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: "1.1rem", color: 'text.secondary' }}>No phone provided</Typography>
                      )}
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemText primary="Birthday" secondary={formatDate(member.MMERGE6) || "Not provided"} primaryTypographyProps={{ sx: { fontSize: "1.1rem" } }} secondaryTypographyProps={{ sx: { fontSize: "1.1rem" } }} />
                  </ListItem>
                  {user?.email === member.email && (
                    <>
                      <Divider component="li" />
                      <ListItem>
                        <Button
                          startIcon={<EditIcon />}
                          onClick={e => {
                            e.stopPropagation() // Prevent row collapse
                            handleEdit(member)
                          }}
                          variant="outlined"
                          fullWidth
                        >
                          Edit My Information
                        </Button>
                      </ListItem>
                    </>
                  )}
                </List>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    )
  }

  const handleEdit = member => {
    setSelectedMember(member)
    setEditOpen(true)
  }

  const handleSave = async formData => {
    try {
      const { photoUrl, ...updates } = formData
      await axios.post("/.netlify/functions/update-member", {
        email: selectedMember.email,
        updates,
        photoUrl
      })

      // Refresh directory data
      fetchDirectoryData()
      setEditOpen(false)
    } catch (error) {
      setError(error.response?.data?.error || "Failed to update information")
    }
  }

  if (loading) return <Box sx={{ p: 3 }}>Loading...</Box>
  if (error) return <Box sx={{ p: 3, color: "error.main" }}>{error}</Box>

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ width: "100%", mb: 2, overflow: "hidden" }}>
          <AppBar position="static">
            <Toolbar sx={{ flexDirection: isMobile ? "column" : "row", padding: isMobile ? 1 : "inherit" }}>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  flexGrow: 1,
                  width: isMobile ? "100%" : "auto",
                  textAlign: isMobile ? "center" : "left",
                  mb: isMobile ? 1 : 0
                }}
              >
                Directory Members ({filteredAndSortedMembers.length})
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: isMobile ? "center" : "flex-end",
                  width: isMobile ? "100%" : "auto"
                }}
              >
                <Tooltip title="Toggle Dark Mode">
                  <IconButton color="inherit" onClick={toggleColorMode} sx={{ mx: 1 }}>
                    {mode === "dark" ? <Brightness7Icon sx={{ fontSize: 28 }} /> : <Brightness4Icon sx={{ fontSize: 28 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Help">
                  <IconButton color="inherit" onClick={() => setHelpOpen(true)} sx={{ mx: 1 }}>
                    <HelpIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton
                    color="inherit"
                    onClick={() => {
                      const controller = new AbortController()
                      fetchDirectoryData(controller.signal)
                    }}
                    sx={{ mx: 1 }}
                  >
                    <RefreshIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Logout">
                  <IconButton color="inherit" onClick={handleLogout} sx={{ mx: 1 }}>
                    <LogoutIcon sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
                {user && user.email && (
                  <Tooltip title="Edit My Information">
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        const userMember = data.find(member => member.email === user.email)
                        if (userMember) {
                          handleEdit(userMember)
                        } else {
                          alert("Your information was not found in the directory.")
                        }
                      }}
                      sx={{
                        p: 0,
                        ml: 2,
                        mr: 0 // Remove right margin to push it all the way to the right
                      }}
                      disabled={!data.some(member => member.email === user.email)}
                    >
                      <MemberAvatar
                        member={data.find(member => member.email === user.email) || { email: user.email }}
                        mode={mode}
                        width={44}
                        height={44}
                        fontSize="1.3rem"
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Toolbar>
          </AppBar>

          <Drawer
            anchor="right"
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: { xs: "100%", sm: "400px" },
                padding: 3,
                overflowY: "auto"
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5">Directory Help</Typography>
                <IconButton onClick={() => setHelpOpen(false)} edge="end">
                  <CloseIcon />
                </IconButton>
              </Box>
              <ReactMarkdown>
                {`
# Welcome to the LKAPS Directory!

This tool is designed to help you easily find and communicate with other members of our group. 

## 🔍 How to Search & Sort
- **Using the Search Bar:** Type a name, email, or phone number into the search box at the top to instantly filter the list.
- **Sorting the List:** Click the **Name** column header at the top of the grid to magically re-sort everyone alphabetically!

## 📞 Contacting Members
We've made reaching out as simple as clicking a button!
- **Email [📧]:** Click the blue envelope icon to instantly draft an email to the member.
- **Call [📞]:** Click the green phone icon to instantly dial the member's number.
- **Text [💬]:** Click the purple text message icon to instantly send them an SMS text!
*(Note: If a button does not open your app directly, your computer may lack a default app. Don't worry—the email and phone number are printed right next to the buttons so you can always copy them manually!)*

## ✏️ Updating Your Info & Profile Photo
If your phone number, name, birthday, or profile photo needs updating, you can edit it yourself! 
1. **On Desktop:** Click your profile avatar button in the top-right corner of the header.
2. **On Mobile:** Tap on your name in the directory list to expand your details, then tap the **Edit My Information** button. Or, tap your profile avatar button in the top-right corner.
3. **Manage Profile Photo:** Inside the pop-up, click **Upload Photo** to select and crop a profile picture (we automatically crop and resize it to a perfect 300x300px square). Click **Remove** if you want to clear your custom photo.
4. **Edit Text Fields:** Make any updates to your name, phone number, or birthday.
5. Click **Save** to apply the changes across the directory.

*(Note: The directory displays your custom uploaded photo. If none is uploaded, it falls back to your Gravatar profile picture, and then to your initials if neither is available.)*

## 🌙 Dark Mode & Light Mode
If you prefer a darker screen that is easier on the eyes, click the **Moon / Sun icon** located at the top-right corner of the application to switch visual themes!

---

## Feedback & Ideas
We welcome your feedback! Please email ken@lkaps.org with:
- Suggestions for new features
- Ideas for improving the directory
- Any issues you encounter

Your input helps make this directory better for everyone!
                `}
              </ReactMarkdown>
            </Box>
          </Drawer>

          <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: { fontSize: "1.1rem" }
              }}
            />
          </Paper>

          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <LinearProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: isMobile ? "auto" : 750 }}>
                {!isMobile && (
                  <TableHead>
                    <TableRow>
                      {columns.map(column => (
                        <TableCell
                          key={column.id}
                          align={column.id === "name" || column.id === "email" ? "left" : "center"}
                          sx={{
                            fontSize: "1.2rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            userSelect: "none",
                            backgroundColor: mode === "light" ? "#f5f5f5" : "#1a1a1a"
                          }}
                          onClick={() => handleSort(column.id)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: column.id === "name" || column.id === "email" ? "flex-start" : "center"
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {filteredAndSortedMembers.map((member, index) =>
                    isMobile ? (
                      <MobileRow key={member.email} member={member} index={index} />
                    ) : (
                      <TableRow
                        key={member.email}
                        sx={{
                          backgroundColor: mode === "light" ? (index % 2 === 0 ? "white" : "#fafafa") : index % 2 === 0 ? "#121212" : "#1a1a1a",
                          "&:hover": {
                            backgroundColor: mode === "light" ? "#f5f5f5" : "#202020"
                          }
                        }}
                      >
                        <TableCell sx={{ fontSize: "1.1rem", minWidth: "250px" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <MemberAvatar member={member} mode={mode} width={48} height={48} />
                            <span>{member.FNAME ? `${member.FNAME} ${member.LNAME || ""}`.trim() : ""}</span>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "1.1rem", textAlign: "left", minWidth: "250px" }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title={`Email: ${member.email}`}>
                              <IconButton
                                component="a"
                                href={`mailto:${member.email}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                color="primary"
                                edge="start"
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                            {member.email}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: "1.1rem", textAlign: "left", minWidth: "250px" }}>
                          {member.PHONE && member.PHONE.replace(/\D/g, "").length >= 10 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {formatPhone(member.PHONE)}
                              <Tooltip title="Call">
                                <IconButton
                                  component="a"
                                  href={`tel:${member.PHONE.replace(/\D/g, "")}`}
                                  onClick={(e) => e.stopPropagation()}
                                  color="success"
                                >
                                  <PhoneIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Text">
                                <IconButton
                                  component="a"
                                  href={`sms:${member.PHONE.replace(/\D/g, "")}`}
                                  onClick={(e) => e.stopPropagation()}
                                  color="secondary"
                                >
                                  <SmsIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : ""}
                        </TableCell>
                        <TableCell sx={{ fontSize: "1.1rem", textAlign: "center" }}>{formatDate(member.MMERGE6) || ""}</TableCell>
                      </TableRow>
                    )
                  )}
                  {filteredAndSortedMembers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 1 : 4} align="center" sx={{ fontSize: "1.1rem" }}>
                        {searchTerm ? "No matching members found" : "No members found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
      <EditDialog open={editOpen} handleClose={() => setEditOpen(false)} member={selectedMember} handleSave={handleSave} currentUser={user} mode={mode} />
    </Box>
  )
}

export default Directory
